import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRightIcon } from "lucide-react";
import { TreatedImage } from "./TreatedImage";
import { BODY, EYEBROW, PANEL_HEADING } from "../utils/typography";
const ease = [0.23, 1, 0.32, 1] as const;
const PORTRAIT_URL = "https://cdn.magicpatterns.com/uploads/vocf1y2AQQYxjkG8kUjPXn/face.jpg";
const RESUME_URL = "https://cdn.magicpatterns.com/uploads/iiHMpP6CM2Yvm1QSgNbeod/Alyssa-Retza-Resume_Final.pdf";
const rows = [{
  label: "Currently",
  value: "Director of Digital Strategy & Web Product | Co-Chair, AI Governance Council"
}, {
  label: "What I do",
  value: "I bring clarity to complex digital systems, shape the brands and stories that make them meaningful, and explore what technological progress means for the people whose lives it shapes."
}];

/** Intro: the portrait sits on the cream page as its own print and overlaps the separate espresso box. */
export function AboutSection() {
  const reduce = useReducedMotion();
  const inView = {
    once: true,
    amount: 0.3
  } as const;
  return <section id="about" aria-labelledby="intro-heading" className="w-full scroll-mt-28 bg-cream px-6 pb-24 md:scroll-mt-36 md:px-12 md:pb-32 lg:px-20 lg:pb-40" data-id="element-19" data-mp="xij8r3p0l40l">
      <div className="grid grid-cols-1 lg:mx-auto lg:w-[88%] lg:max-w-[1280px] lg:grid-cols-12 lg:items-center" data-id="element-20" data-mp="nszmrp608u1i">
        <motion.div className="relative z-10 -mb-20 mx-auto w-[70%] max-w-[18rem] -rotate-2 rounded-3xl shadow-[0_24px_48px_-16px_rgba(15,12,10,0.45)] md:-mb-24 md:max-w-xs lg:col-span-5 lg:col-start-1 lg:row-start-1 lg:mb-0 lg:ml-0 lg:w-full lg:max-w-none" initial={reduce ? false : {
        opacity: 0,
        y: 16,
        rotate: -4
      }} whileInView={{
        opacity: 1,
        y: 0,
        rotate: -2
      }} viewport={inView} transition={{
        duration: 0.3,
        ease,
        delay: 0.12
      }} data-id="element-21" data-mp="1biu16b5z2qnv">
          <TreatedImage src={PORTRAIT_URL} alt="Alyssa Retza" className="aspect-[4/5] w-full rounded-3xl bg-bark" data-id="element-22" data-mp="3r9gj5r0uncjo" />
        </motion.div>

        <motion.div className="rounded-3xl bg-espresso px-8 pb-10 pt-28 md:px-14 md:pb-16 md:pt-36 lg:col-span-8 lg:col-start-5 lg:row-start-1 lg:my-8 lg:flex lg:flex-col lg:justify-center lg:self-stretch lg:py-16 lg:pl-32 lg:pr-20 xl:pl-36" initial={reduce ? false : "hidden"} whileInView="shown" variants={{
        hidden: {
          opacity: 0,
          y: 24,
          scale: 0.98
        },
        shown: {
          opacity: 1,
          y: 0,
          scale: 1
        }
      }} viewport={inView} transition={{
        duration: 0.3,
        ease
      }} data-id="element-23" data-mp="1xpoajpg248gm">
          <h2 id="intro-heading" aria-label="Hi, I’m Alyssa." className={`${PANEL_HEADING} text-cream`} data-id="element-24" data-mp="152bve0c872xc">
            <span aria-hidden="true" className="-my-[0.06em] block overflow-hidden py-[0.06em]" data-id="element-25" data-mp="103h6qhrkuk5b">
              {/* Driven by the panel's in-view state: the clipped word itself never "intersects" */}
              <motion.span className="block" variants={{
              hidden: {
                y: "110%"
              },
              shown: {
                y: "0%"
              }
            }} transition={{
              duration: 0.3,
              ease,
              delay: 0.15
            }} data-id="element-26" data-mp="2uhi5l2n4n75e">
                Hi, I’m Alyssa<span className="text-mustard" data-id="element-27" data-mp="36ysyfhwz176g">.</span>
              </motion.span>
            </span>
          </h2>

          <dl className="mt-12 space-y-8 md:mt-14 md:space-y-10" data-id="element-28" data-mp="3br46j81v7za3">
            {rows.map(row => <div key={row.label} data-id="element-29" data-mp="3gp5mvl7wpkhv">
                <dt className={`${EYEBROW} text-mustard`} data-id="element-30" data-mp="2jzem5490v13m">{row.label}</dt>
                <dd className={`mt-2 max-w-2xl ${BODY} text-cream`} data-id="element-31" data-mp="2k1hkcbkbw0jf">{row.value}</dd>
              </div>)}
          </dl>

          <div className="mt-12 flex flex-wrap gap-3 md:mt-14" data-id="element-32" data-mp="2t0coli3tb0k3">
            <a href={RESUME_URL} target="_blank" rel="noopener noreferrer" aria-label="View my résumé (PDF, opens in a new tab)" className="flex h-12 items-center gap-3 whitespace-nowrap rounded-full bg-rust px-6 text-sm font-medium uppercase text-cream transition-colors duration-150 ease-out hover:bg-mustard hover:text-espresso" data-id="element-33" data-mp="2t25yr7dmw2wu">
              View my résumé
              <ArrowUpRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-34" data-mp="1h7c0qx2r85ab" />
            </a>
            <a href="#contact" className="flex h-12 items-center whitespace-nowrap rounded-full px-6 text-sm font-medium uppercase text-cream ring-1 ring-cream/20 transition-colors duration-150 ease-out hover:bg-cream hover:text-espresso" data-id="element-35" data-mp="1v6btogxav6ca">
              Let’s talk
            </a>
          </div>
        </motion.div>
      </div>
    </section>;
}